import type { Faq } from 'libs/http/api/content/content.types';

import type { AccessRoute } from 'ui/organisms/AccessRoutes/AccessRoutes.types';
import type { EditorialStep } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection.types';
import type { CompareMatrixColumn, CompareMatrixRow } from 'ui/molecules/CompareMatrix/CompareMatrix.types';
import type { FeedAlert } from 'ui/molecules/PerpetualFeedCard/PerpetualFeedCard.types';
import type { RoutingDestination } from 'ui/molecules/AlertRoutingBoard/AlertRoutingBoard.types';

type Translate = (key: string) => string;

export const getFeedAlerts = (t: Translate): FeedAlert[] => [
  {
    company: 'Meridian Trade GmbH',
    registry: 'Handelsregister (DE)',
    change: t('New shareholder filed'),
    variant: 'ownership',
    time: '06:42',
    filing: t('Shareholder list — change of shareholding'),
    from: 'Weber Beteiligungen GmbH · 100%',
    to: 'Weber Beteiligungen GmbH · 62% · NovaPart Capital S.à r.l. · 38%',
    reference: 'HRB 88231 · filed 08 Jul 2026',
  },
  {
    company: 'Northgate Logistics Ltd',
    registry: 'Companies House (UK)',
    change: t('Status → Liquidation'),
    variant: 'status',
    time: '07:15',
    filing: t('Resolution for winding up (LRESSP)'),
    from: 'Active',
    to: 'Liquidation',
    reference: 'Company 07216654 · filed 08 Jul 2026',
  },
  {
    company: 'Baltic Payments UAB',
    registry: 'Registrų centras (LT)',
    change: t('Director resigned'),
    variant: 'director',
    time: '08:03',
    filing: t('Change of management body member'),
    from: '3 directors registered',
    to: '2 directors registered',
    reference: 'JAR code 304812277 · filed 08 Jul 2026',
  },
  {
    company: 'Corvara Holdings B.V.',
    registry: 'KVK (NL)',
    change: t('Ownership chain updated'),
    variant: 'ownership',
    time: '08:47',
    filing: t('UBO register update'),
    from: '1 ultimate parent on record',
    to: 'New intermediate holding · Corvara Group S.A. (LU)',
    reference: 'KVK 63118742 · filed 08 Jul 2026',
  },
  {
    company: 'Solent Fabrication Ltd',
    registry: 'Companies House (UK)',
    change: t('Annual accounts filed'),
    variant: 'accounts',
    time: '09:20',
    filing: t('Full accounts made up to 31 Mar 2026'),
    from: 'Last accounts: FY2024',
    to: 'FY2025 accounts on record · digitised',
    reference: 'Company 04588112 · filed 08 Jul 2026',
  },
  {
    company: 'Aldermoor Estates Ltd',
    registry: 'Companies House (UK)',
    change: t('Director appointed'),
    variant: 'director',
    time: '09:41',
    filing: t('Appointment of director (AP01)'),
    from: '2 directors registered',
    to: '3 directors registered',
    reference: 'Company 11302876 · filed 08 Jul 2026',
  },
  {
    company: 'Verano Foods S.L.',
    registry: 'Registro Mercantil (ES)',
    change: t('Registered address changed'),
    variant: 'address',
    time: '10:06',
    filing: t('Change of registered office'),
    from: 'Calle Serrano 41, Madrid',
    to: 'Av. Diagonal 611, Barcelona',
    reference: 'NIF B-84920115 · filed 08 Jul 2026',
  },
  {
    company: 'Helvia Components AG',
    registry: 'Zefix (CH)',
    change: t('New shareholder filed'),
    variant: 'ownership',
    time: '10:29',
    filing: t('Mutation — change in ownership'),
    from: 'Family holding · 100%',
    to: 'Family holding · 74% · Industrieholding Ost AG · 26%',
    reference: 'CHE-214.882.410 · filed 08 Jul 2026',
  },
  {
    company: 'Riva Marine S.r.l.',
    registry: 'Registro Imprese (IT)',
    change: t('Status → Dissolved'),
    variant: 'status',
    time: '10:52',
    filing: t('Cancellation from the register'),
    from: 'Active',
    to: 'Dissolved',
    reference: 'REA MI-1928475 · filed 08 Jul 2026',
  },
  {
    company: 'Danske Komponenter ApS',
    registry: 'CVR (DK)',
    change: t('Annual accounts filed'),
    variant: 'accounts',
    time: '11:14',
    filing: 'Årsrapport 2025',
    from: 'Last accounts: FY2024',
    to: 'FY2025 accounts on record · digitised',
    reference: 'CVR 38821910 · filed 08 Jul 2026',
  },
];

export const getChangeTypeChips = (t: Translate): string[] => [
  t('New shareholder filed'),
  t('Director appointed'),
  t('Status changed to dissolved'),
  t('Annual accounts filed'),
  t('Registered address changed'),
  t('Shareholder exited'),
  t('Director resigned'),
  t('Status changed to liquidation'),
  t('Accounts overdue'),
  t('Company name changed'),
  t('Shareholding percentage changed'),
  t('Officer details changed'),
  t('Insolvency filing'),
  t('Confirmation statement filed'),
  t('Trading name added'),
  t('Ownership chain restructured'),
  t('Company secretary changed'),
  t('Winding-up petition'),
  t('Financial year end changed'),
  t('Legal form changed'),
  t('New ultimate beneficial owner'),
  t('Persons with significant control changed'),
  t('Administration notice'),
  t('Charge registered'),
  t('Jurisdiction changed'),
  t('UBO removed'),
  t('Auditor changed'),
  t('Company restored'),
  t('Charge satisfied'),
  t('Activity code changed'),
  t('Parent company changed'),
  t('VAT deregistered'),
  t('Branch opened or closed'),
  t('Group structure changed'),
  t('Sanctions list addition'),
  t('Share capital changed'),
  t('PEP match'),
  t('Court judgment filed'),
];

export const getCompareColumns = (t: Translate): CompareMatrixColumn[] => [
  { name: t('Periodic review'), tag: t('The old model') },
  { name: t('Perpetual KYB'), highlight: true, tag: t('The right way') },
];

export const getCompareRows = (t: Translate): CompareMatrixRow[] => [
  {
    metric: t('Detection speed'),
    cells: [
      { text: t('Once in a while — every 30–90 days') },
      { text: t("As it happens — the day it's filed"), positive: true },
    ],
  },
  {
    metric: t('Exposure window'),
    cells: [
      { text: t('Up to 243 days blind between reviews') },
      { text: t('Same-day — no gap to sit in'), positive: true },
    ],
  },
  {
    metric: t('Trigger'),
    cells: [
      { text: t('Runs on a fixed calendar') },
      { text: t('Runs on the data — reacts to the filing'), positive: true },
    ],
  },
  {
    metric: t('Wasted checks'),
    cells: [
      { text: t('Most scheduled checks find nothing changed') },
      { text: t('Only real changes surface — no noise'), positive: true },
    ],
  },
  {
    metric: t('Cost model'),
    cells: [
      { text: t('Pay per lookup — you pay to find nothing') },
      { text: t('Pay once per company — unlimited updates'), positive: true },
    ],
  },
  {
    metric: t('Audit trail'),
    cells: [
      { text: t('Shows the last review date only') },
      { text: t('Source registry + filing timestamp on every change'), positive: true },
    ],
  },
  {
    metric: t('Answers "when did you know?"'),
    cells: [
      { text: t('Not until the next scheduled review') },
      { text: t('To the day, at the source'), positive: true },
    ],
  },
];

export const getMonitorSteps = (t: Translate): EditorialStep[] => [
  {
    num: '01',
    iconType: 'i-73',
    title: t('Status & insolvency events'),
    description: t(
      'Dissolution, liquidation, administration, strike-off notices and dormancy — flagged the day they hit the register.',
    ),
  },
  {
    num: '02',
    iconType: 'i-2',
    title: t('UBO & ownership changes'),
    description: t(
      'New beneficial owners, shareholding transfers and control changes that trigger re-screening obligations.',
    ),
  },
  {
    num: '03',
    iconType: 'i-85',
    title: t('Director appointments & exits'),
    description: t(
      'New officers, resignations and disqualifications — including directors linked to previously failed entities.',
    ),
  },
  {
    num: '04',
    iconType: 'i-27',
    title: t('Filings & accounts'),
    description: t(
      'New annual accounts, overdue filings and confirmation statements — the earliest distress signals before a default.',
    ),
  },
  {
    num: '05',
    iconType: 'i-110',
    title: t('Name & address changes'),
    description: t(
      'Legal name changes, registered office moves and jurisdiction shifts that silently break your records.',
    ),
  },
  {
    num: '06',
    iconType: 'i-115',
    title: t('Group structure changes'),
    description: t(
      'New subsidiaries, mergers and parent changes across the corporate group — mapped to your exposure.',
    ),
  },
];

export const getRoutingDestinations = (t: Translate): RoutingDestination[] => [
  {
    name: t('Platform'),
    description: t('Portfolios, alert queue, audit trail — in the browser.'),
    links: [{ label: t('Start monitoring'), href: '/book-a-demo' }],
  },
  {
    name: 'API',
    pill: 'MCP server',
    description: t('Webhook push per filing — into any flow, or any AI agent.'),
    links: [{ label: t('Get API keys'), href: '/book-a-demo' }],
  },
  {
    name: 'CRM',
    description: (
      <>
        {t('Native apps for')} <b>Salesforce</b> {t('and')} <b>HubSpot</b> {t('— alerts on the account record.')}{' '}
        <b>Dynamics</b> {t('via sync.')}
      </>
    ),
    links: [
      { label: 'Salesforce', href: 'https://appexchange.salesforce.com/appxListingDetail?listingId=7d8cb601-add2-47f7-a345-4008cbb0cfe4' },
      { label: 'HubSpot', href: 'https://ecosystem.hubspot.com/marketplace/listing/global-database-intelligence-enrichment' },
    ],
  },
  {
    name: t('AI Connectors'),
    description: t('"What changed in my suppliers this week?" — free for Claude and ChatGPT.'),
    links: [
      { label: 'Claude', href: 'https://claude.ai/new#directory/connectors/global-database' },
      { label: 'ChatGPT', href: 'https://chatgpt.com/plugins/plugin_asdk_app_696f807d21a481918a1ed1f43d719ce9' },
    ],
  },
  {
    name: t('Bulk Data Feeds'),
    description: t('Daily delta files — only what changed. Redistribution licensing available.'),
    links: [{ label: t('Talk to data sales'), href: '/book-a-demo' }],
  },
];

export const getPerpetualLoopSteps = (t: Translate): AccessRoute[] => [
  {
    name: t('Collect and store'),
    desc: t(
      'Primary data pulled daily from 400+ government registries and stored on our own servers in Germany. Every search, API call and alert is answered from that copy.',
    ),
  },
  {
    name: t('Detect real changes'),
    desc: t(
      "Each day's filings are compared against the copy we hold. Formatting noise is discarded, and every real change is stamped with its source registry and filing date.",
    ),
  },
  {
    name: t('Resolve and normalise'),
    desc: t(
      "Names, addresses and identifiers are reconciled across jurisdictions. Ownership chains update in context, so one company's change reflects across its group.",
    ),
  },
  {
    name: t('Digitise filed accounts'),
    desc: t(
      'Accounts filed as PDFs or scans become structured fields with OCR and AI — balance sheet, P&L and cash flow. Some jurisdictions carry 20 years of history.',
    ),
  },
  {
    name: t('Propagate same day'),
    desc: t(
      'Every monitored company touched by a filing updates the same day. The alert reaches you by webhook, bulk feed, platform, or your AI connector.',
    ),
  },
];

export const getPricingColumns = (
  t: Translate,
): { label: string; value: string; description: string; highlight?: boolean }[] => [
  {
    label: t('Periodic · pay per lookup'),
    value: t('20,000 calls / yr'),
    description: t('5,000 suppliers checked quarterly. 19,800 of those lookups return no change.'),
  },
  {
    label: t('Perpetual · pay per relationship'),
    value: t('~200 alerts / yr'),
    description: t('Only the changes that happened, delivered the day they were filed. No throttle, no per-call cost.'),
    highlight: true,
  },
];

export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'what-is-perpetual-kyb',
    question: t('What is perpetual KYB monitoring?'),
    answer: t(
      'Perpetual KYB is the continuous, event-driven verification of a business customer after onboarding. Instead of re-verifying on a fixed schedule, the system tracks ownership, directors, status, and filings, and triggers a review only when something material changes. It is also called pKYB, continuous KYB, or event-driven KYB.',
    ),
  },
  {
    id: 'periodic-vs-perpetual',
    question: t('What is the difference between periodic KYB and perpetual KYB?'),
    answer: t(
      'Periodic KYB reviews business customers on a fixed schedule, typically annually or quarterly. Perpetual KYB monitors continuously and reviews when the data changes. The difference shows up in detection speed: up to 12 months under an annual cycle versus <b>the same day the filing is made</b> under perpetual monitoring.',
    ),
  },
  {
    id: 'is-perpetual-kyb-required',
    question: t('Is perpetual KYB legally required?'),
    answer: t(
      'Not by name. No major framework — FinCEN CDD, 6AMLD, the UK Money Laundering Regulations, FATF Recommendation 10 — uses the term. All of them, however, require <b>ongoing monitoring of the business relationship</b>, including keeping customer information up to date. Periodic reviews satisfy the literal rule for low-risk relationships; recent enforcement actions increasingly cite detection gaps rather than onboarding errors.',
    ),
  },
  {
    id: 'how-often-kyb-reviews',
    question: t('How often should KYB reviews be conducted?'),
    answer: t(
      'Match frequency to risk. Low-risk: every 24 to 36 months. Medium-risk: annually. High-risk: every six months or less. For very high-risk relationships, continuous monitoring is the more defensible model. Most regulators expect a documented risk-based approach rather than a uniform calendar.',
    ),
  },
  {
    id: 'kyb-risk-window',
    question: t('What is the KYB risk window?'),
    answer: t(
      'The time between when a material change happens to a counterparty and when your compliance team finds out. With annual reviews the window can stretch to 12 months. With registry-source, event-driven monitoring it collapses to the same day the filing is made.',
    ),
  },
  {
    id: 'kyb-review-triggers',
    question: t('What events should trigger a KYB review?'),
    answer: t(
      'High-priority triggers: new beneficial owner, director appointment or resignation, company status change, insolvency filing, jurisdiction change, merger or acquisition. Lower-priority triggers: business activity reclassification, address change, contact update. Effective programs treat these differently — same-day alerts for material changes, batched review for the rest.',
    ),
  },
  {
    id: 'alert-fatigue',
    question: t('What causes alert fatigue in continuous KYB monitoring?'),
    answer: t(
      'Polling-based detection. Tools that compare daily registry snapshots flag any difference, including formatting noise: reformatted addresses, capitalisation changes, timestamp refreshes, re-sorted director lists. Analysts spend hours dismissing non-material differences and start to deprioritise the queue.',
    ),
  },
  {
    id: 'event-driven-vs-polling',
    question: t('What is the difference between event-driven and polling-based KYB monitoring?'),
    answer: t(
      "Polling-based monitoring queries registries on a fixed schedule and flags any difference between today's snapshot and yesterday's. Event-driven monitoring detects actual changes at the registry source as filings are made. Both can be marketed as continuous — <b>only one reduces analyst workload</b>. Event-driven detection typically cuts alert volume by 80 to 90%.",
    ),
  },
  {
    id: 'workload-reduction',
    question: t('How much can perpetual KYB reduce compliance workload?'),
    answer: t(
      'Industry estimates put the saving at 50 to 90% of routine review labour, depending on the maturity of the previous program and the quality of change detection. Most periodic reviews find that nothing changed. The savings only materialise if detection is event-driven; polling-based tools often add noise on top of the original review cadence.',
    ),
  },
  {
    id: 'periodic-and-perpetual-together',
    question: t('Should I run periodic KYB and perpetual KYB together?'),
    answer: t(
      'For most mature programs, yes. Ownership, directors, and status changes belong on event-driven monitoring. Annual financial filings, full relationship attestation, and risk-rating reviews still benefit from a scheduled cadence. This hybrid is what most regulators implicitly expect when they reference a risk-based approach.',
    ),
  },
];
